import { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Badge, CollapsibleSection, Pagination, RegionState } from '@freelancelabs/components';
import { BriefCaseOutline } from '@freelancelabs/icons';

import { AccountSocietiesQuery, AccountsDocument } from '../../graphql/__generated__/types';
import AccountsTable from './AccountsTable';
import { accountsRefusalOf } from './accountsRefusal';

type Society = NonNullable<NonNullable<AccountSocietiesQuery['accountSocieties']>[number]>;

const PAGE_SIZE = 25;

interface Props {
    society: Society;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Dénominateur du « X / 6 », lu une fois par la page sur `portalModules`. */
    moduleTotal: number | null;
}

/**
 * Un en-tête de groupe de la page « Comptes » : une société mère, et ses comptes au
 * dépliage (PCG-333). Même patron que `ServiceSectionPanel` de « Mon activité ».
 *
 * La requête ne part qu'au dépliage (`skip: !open`), et c'est tout l'intérêt :
 * `<details>` garde son panneau dans le DOM même replié, donc une requête montée
 * partirait une fois par société au premier rendu, ce que replier la liste voulait
 * justement éviter. Le composant reste MONTÉ, la page atteinte survit à un repli, et
 * Apollo répond au ré-ouvrage depuis son cache.
 *
 * L'en-tête ne porte AUCUN lien : il n'existe pas encore de fiche de société, et un
 * geste inerte promettrait un écran. Même arbitrage que `ServiceSectionPanel`. Les
 * comptes, eux, mènent chacun à leur fiche depuis la colonne « actions ».
 */
const AccountSocietyPanel = ({ society, open, onOpenChange, moduleTotal }: Props) => {
    const [page, setPage] = useState(1);

    const rootId = society.rootOrganizationId;

    const { data, loading, error } = useQuery(AccountsDocument, {
        variables: {
            rootId,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
        },
        skip: !open || !rootId,
        fetchPolicy: 'cache-and-network',
    });

    const rows = data?.accounts?.items ?? [];

    // Le compteur du groupe vient de l'index, jamais de la page dépliée. Il compte la
    // société entière du périmètre, ce que `accounts(rootId:)` pagine.
    const total = society.accountCount ?? 0;
    const openCount = society.portalEnabledCount ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const countChip =
        society.accountCount === null || society.accountCount === undefined
            ? undefined
            : `${c('Account societies').ngettext(msgid`${total} account`, `${total} accounts`, total)} · ${c(
                  'Account societies'
              ).ngettext(msgid`${openCount} open`, `${openCount} open`, openCount)}`;

    const rangeStart = (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = (page - 1) * PAGE_SIZE + rows.length;
    const summary = c('Account societies').ngettext(
        msgid`${rangeStart}–${rangeEnd} of ${total} account`,
        `${rangeStart}–${rangeEnd} of ${total} accounts`,
        total
    );

    const refusal = error ? accountsRefusalOf(error) : null;

    /**
     * Un panneau vide sous un en-tête qui annonce des comptes : l'index et la page se
     * sont contredits, ce que l'API documente pour une société sortie du périmètre
     * (elle rend une page vide plutôt qu'un refus). Rendu sans phrase, il se lirait
     * « cette société n'a aucun compte », faux sous un en-tête qui en compte.
     */
    const state = refusal ? (
        <RegionState tone="error" title={refusal.title} description={refusal.guidance} />
    ) : !loading && rows.length === 0 && open ? (
        <RegionState
            title={c('Account societies').t`This company came back with no account.`}
            description={c('Account societies')
                .t`Its counter says otherwise, so something changed between the two queries, or it is no longer within your perimeter.`}
        />
    ) : null;

    return (
        <CollapsibleSection
            className="fl-collapsible-section--group fl-collapsible-section--white-head"
            // Le pictogramme de l'entrée « Comptes » du rail, le même sur toutes les
            // sections : il marque le début de la ligne, le nom distingue.
            icon={<BriefCaseOutline size={16} />}
            open={open}
            onOpenChange={onOpenChange}
            title={society.rootOrganizationName ?? c('Account societies').t`Company with no name`}
            meta={
                <Badge category="default" size="small">
                    {society.siren
                        ? c('Account societies').t`SIREN ${society.siren}`
                        : c('Account societies').t`SIREN —`}
                </Badge>
            }
            count={countChip}
        >
            <AccountsTable
                rows={rows}
                moduleTotal={moduleTotal}
                nested
                loading={loading && rows.length === 0}
                hasError={Boolean(error)}
                state={state}
            />

            {/* Pas de pager sous un panneau vide, une erreur, ou une société tenant sur
                une page : « 1–1 sur 1 » n'offre aucun geste. */}
            {!error && rows.length > 0 && pageCount > 1 && (
                <div className="px-4">
                    <Pagination
                        page={page}
                        pageSize={PAGE_SIZE}
                        totalCount={total}
                        summary={summary}
                        onPageChange={setPage}
                    />
                </div>
            )}
        </CollapsibleSection>
    );
};

export default AccountSocietyPanel;
